import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  IndianRupee,
  TrendingDown,
  ShoppingCart,
  ArrowDown,
  Sparkles,
} from "lucide-react";

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "All time", days: 365 },
];

export function AdminFunnelReport() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await adminAPI.analyticsV2Funnel(d);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminAPI.analyticsV2Funnel(30);
        if (!active) return;
        setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load funnel");
      }
    })();
    return () => { active = false; };
  }, []);

  const handleBackfill = async () => {
    if (!window.confirm("Import historic captured payments into analytics_events? (Idempotent — safe to re-run.)")) return;
    setBackfilling(true);
    try {
      const res = await adminAPI.analyticsV2Backfill();
      toast.success(`Inserted ${res.data.inserted}, skipped ${res.data.skipped_existing}`);
      await load(days);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const funnel = data?.funnel || [];
  const summary = data?.summary || {};
  const maxCount = Math.max(1, ...funnel.map((f) => f.unique_count));

  return (
    <div className="space-y-4" data-testid="admin-funnel-report">
      {/* Header + range selector */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#18181B]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Conversion Funnel
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Unique visitors at each step + step-over-step conversion. Independent of Meta.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => { setDays(r.days); load(r.days); }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === r.days ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`funnel-range-${r.days}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => load(days)} disabled={loading} data-testid="funnel-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={handleBackfill} disabled={backfilling} data-testid="funnel-backfill-btn">
            {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Backfill purchases"}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              icon={ShoppingCart}
              label="Purchases"
              value={summary.purchases ?? 0}
              testid="funnel-summary-purchases"
            />
            <SummaryCard
              icon={IndianRupee}
              label="Revenue"
              value={`₹${Number(summary.revenue || 0).toLocaleString("en-IN")}`}
              highlight
              testid="funnel-summary-revenue"
            />
            <SummaryCard
              icon={Sparkles}
              label="AOV"
              value={`₹${Number(summary.aov || 0).toLocaleString("en-IN")}`}
              testid="funnel-summary-aov"
            />
            <SummaryCard
              icon={TrendingDown}
              label="Overall Conv."
              value={`${summary.overall_conversion_pct ?? 0}%`}
              testid="funnel-summary-conv"
            />
          </div>

          {/* Funnel steps */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="funnel-steps">
            <div className="space-y-2">
              {funnel.map((step, idx) => {
                const widthPct = Math.max(3, (step.unique_count / maxCount) * 100);
                const dropoff =
                  idx > 0 && step.conversion_from_prev_pct !== null
                    ? 100 - step.conversion_from_prev_pct
                    : null;
                return (
                  <div key={step.key}>
                    {idx > 0 && (
                      <div className="flex items-center gap-1.5 pl-3 py-0.5">
                        <ArrowDown className="h-3 w-3 text-slate-400" />
                        <span className="text-[11px] font-medium text-slate-500">
                          {step.conversion_from_prev_pct ?? 0}% converted
                          {dropoff !== null && dropoff > 0 && (
                            <span className="text-red-500 ml-1.5">
                              ({dropoff.toFixed(1)}% dropped)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    <div
                      className="relative flex items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                      data-testid={`funnel-step-${step.key}`}
                    >
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500/15 to-emerald-400/5 transition-all duration-500"
                        style={{ width: `${widthPct}%` }}
                      />
                      <div className="relative flex items-center justify-between w-full px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold text-[#18181B]">
                            {step.label}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                            {step.events_tracked.join(" · ")}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#18181B] tabular-nums">
                            {step.unique_count.toLocaleString("en-IN")}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {step.event_count.toLocaleString("en-IN")} events
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-3">
              Unique = distinct visitor_id per step. Events = raw event count (retries + duplicates counted).
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div
      className={`rounded-xl border p-3 ${highlight ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`h-3.5 w-3.5 ${highlight ? "text-emerald-700" : "text-slate-500"}`} />
        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold tabular-nums ${highlight ? "text-emerald-800" : "text-[#18181B]"}`}>{value}</p>
    </div>
  );
}
